/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Browser-side cookie helpers, for the cookies this app does NOT set — third-party tags that write
 * from JS, which the consent bridge has to clean up when a shopper declines a category.
 *
 * The server-side counterpart is `cookie-utils.server.ts`; it owns everything the app itself sets
 * (and namespaces those names with the site id). Nothing here can touch an `HttpOnly` cookie.
 */

/** Read a cookie value from `document.cookie`. Returns undefined when absent or off-browser. */
export function readCookie(name: string): string | undefined {
    if (typeof document === 'undefined') {
        return undefined;
    }
    for (const entry of document.cookie.split(';')) {
        const separator = entry.indexOf('=');
        if (separator === -1) {
            continue;
        }
        if (entry.slice(0, separator).trim() === name) {
            return entry.slice(separator + 1);
        }
    }
    return undefined;
}

/**
 * Every `domain` scope a cookie on `hostname` could have been set with: host-only (no `domain`
 * attribute), then each parent domain down to two labels.
 *
 * Deleting a cookie requires re-setting it with the same `domain` it was created with, and there is
 * no way to read that back — so every candidate is tried. Assuming the registrable domain is the
 * last two labels is wrong on multi-part public suffixes (`www.example.co.uk` → `co.uk`), where the
 * browser rejects the bare suffix and the real `.example.co.uk` cookie would survive. Generating
 * the whole chain avoids needing a public-suffix list: scopes the browser rejects are ignored
 * silently, so over-generating is harmless.
 */
export function cookieDomainScopes(hostname: string): Array<string | undefined> {
    const labels = hostname.split('.');
    const parents: string[] = [];
    for (let i = 0; i <= labels.length - 2; i += 1) {
        parents.push(`.${labels.slice(i).join('.')}`);
    }
    return [undefined, hostname, ...parents];
}

/**
 * Best-effort removal of every cookie whose name starts with one of `prefixes`.
 *
 * Prefix matching (rather than exact names) is what makes this usable against vendor tags: they
 * version and shard their cookie names — `__attentive_id`, `__attentive_pv`, `AMCV_<orgid>` — so an
 * exact list goes stale silently, leaving cookies behind after a shopper has declined.
 *
 * Only clears cookies on the current path (`/`), which is where tags set them; a cookie scoped to a
 * deeper path would survive. `HttpOnly` cookies are invisible here and cannot be removed this way —
 * those belong to the server.
 */
export function deleteCookiesByPrefix(prefixes: readonly string[]): void {
    if (typeof document === 'undefined' || prefixes.length === 0) {
        return;
    }

    const domainScopes = cookieDomainScopes(window.location.hostname);

    for (const entry of document.cookie.split(';')) {
        const name = entry.split('=')[0]?.trim();
        if (!name || !prefixes.some((prefix) => name.startsWith(prefix))) {
            continue;
        }
        for (const domain of domainScopes) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
                domain ? `; domain=${domain}` : ''
            }`;
        }
    }
}
